import { computed, inject, InjectionToken, Injector } from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'
import { Actions, ofType } from '@ngrx/effects'
import { tapResponse } from '@ngrx/operators'
import type { PartialStateUpdater } from '@ngrx/signals'
import { signalStore, withComputed, withFeature, withHooks, withMethods, withProps, withState } from '@ngrx/signals'
import { Events, injectDispatch, on, withEventHandlers, withReducer } from '@ngrx/signals/events'
import { Store } from '@ngrx/store'
import { EMPTY, exhaustMap, filter, map, switchMap, takeUntil, tap } from 'rxjs'

import { isPaymentRequired } from '@app/core/is-payment-required'
import { nonCancelableRequest } from '@app/core/rxjs/non-cancelable-request'
import { CreditsStore } from '@app/modules/credits/store/credits.store'
import { CreditsNavigationService } from '@app/modules/navigation/services/credits-navigation.service'
import * as fromTopAdmirers from '@app/modules/top-admirers/store'
import { PopoverService } from '@app/overlays/popover/popover.service'
import { ToastService } from '@app/overlays/toast/toast.service'
import { ErrorHandlers } from '@app/services/error-handlers.service'
import { MemberService } from '@app/services/member.service'
import { SiteStore } from '@app/store/site.store'
import * as fromPhotoAccessRequests from '@store/photo-access-requests'
import * as fromProfile from '@store/profile'
import * as fromProfiles from '@store/profiles'

import { conversationEvents } from './conversation.events'
import { injectDefaultErrorHandler } from './default-error-handler'
import { messageFeature } from './message.feature'
import { realtimeMessagingEvents } from './realtime-messaging.events'
import { realtimeMessagingFeature } from './realtime-messaging.feature'
import { sendingEvents } from './sending.events'
import { sendingFeature } from './sending.feature'
import type { Conversation } from '../../models/conversation.model'
import type { ConversationProfile } from '../../models/conversation-profile.model'
import type { MessageSource } from '../../models/message-source.model'
import type { Message } from '../../models/message.model'
import { boostFeature } from '../../modules/boost/store/boost.feature'
import { messagePhotoFeature } from '../../modules/message-photo/components/message-photo/message-photo.feature'
import { readReceiptsEvents } from '../../modules/read-receipts/store/read-receipts.events'
import { readReceiptsFeature } from '../../modules/read-receipts/store/read-receipts.feature'
import { topAdmirerFeature } from '../../modules/top-admirer/store/top-admirer.feature'
import { voiceMessageFeature } from '../../modules/voice-message/components/voice-message/voice-message.feature'
import { ConversationUIService } from '../../services/conversation-ui.service'
import { ConversationService } from '../../services/conversation.service'
import { MessageCacheService } from '../../services/message-cache.service'
import { MessagingService } from '../../services/messaging.service'
import { PopupService } from '@app/modules/popup/popup.service'

interface ConversationStoreConfig {
  source: MessageSource
  anchor_message_timestamp?: string
}

export const CONVERSATION_STORE_CONFIG = new InjectionToken<ConversationStoreConfig>('CONVERSATION_STORE_CONFIG')

export interface ConversationState {
  profileBrowseId: string | undefined
  conversation: Conversation | undefined
  next_cursor: string | undefined
  tip_type: string
  tip: string
  loadInProgress: boolean
  loadMoreInProgress: boolean
  unlockInProgress: boolean
  removeInProgress: boolean
  isSuggestedProfile: boolean
  outbid: boolean | undefined
}

export const initialState: ConversationState = {
  profileBrowseId: undefined,
  conversation: undefined,
  next_cursor: undefined,
  tip_type: '',
  tip: '',
  loadInProgress: false,
  loadMoreInProgress: false,
  unlockInProgress: false,
  removeInProgress: false,
  isSuggestedProfile: false,
  outbid: undefined,
}

function updateConversation(
  conversation: Partial<Conversation>,
): PartialStateUpdater<{ conversation: Conversation | undefined }> {
  return state => ({
    conversation: {
      ...state.conversation!,
      ...conversation,
    },
  })
}

function updateConversationProfile(
  profile: Partial<ConversationProfile>,
): PartialStateUpdater<{ conversation: Conversation | undefined }> {
  return state =>
    state.conversation
      ? { conversation: { ...state.conversation, profile: { ...state.conversation.profile, ...profile } } }
      : {}
}

export const ConversationStore = signalStore(
  withState(initialState),
  withFeature(() => messageFeature()),
  withFeature(() => realtimeMessagingFeature()),
  withFeature(() => boostFeature()),
  withFeature(() => readReceiptsFeature()),
  withFeature(() => sendingFeature()),
  withFeature(() => messagePhotoFeature()),
  withFeature(() => voiceMessageFeature()),
  withFeature(() => topAdmirerFeature()),
  withProps(() => ({
    source: inject(CONVERSATION_STORE_CONFIG).source,
    _anchor_message_timestamp: inject(CONVERSATION_STORE_CONFIG).anchor_message_timestamp,
  })),
  withComputed(
    (store, globalStore = inject(Store), memberService = inject(MemberService), siteStore = inject(SiteStore)) => {
      const canGrant = computed(() => {
        const profileId = store.conversation()?.profile.id

        return profileId
          ? globalStore.selectSignal(fromPhotoAccessRequests.Selectors.canGrantAccess(profileId))()
          : false
      })

      const canRequest = computed(() => {
        const profileId = store.conversation()?.profile.id

        return profileId
          ? globalStore.selectSignal(fromPhotoAccessRequests.Selectors.canCreateRequestAccess(profileId))()
          : false
      })

      return {
        canGrant,
        canRequest,
        canRequestOrShareAccess: computed(() => {
          if (!siteStore.features().private_content) {
            return false
          }
          return memberService.account.benefactor ? canRequest() : canGrant()
        }),
        canReport: computed(() => {
          const conversation = store.conversation()
          return conversation && !conversation.locked && !conversation.profile.already_reported
        }),
        canRemove: computed(() => {
          return store.messages().length > 0 || (store.conversation()?.locked_messages_count ?? 0) > 0
        }),
      }
    },
  ),
  withMethods(
    (
      store,
      creditsNavigationService = inject(CreditsNavigationService),
      dispatch = injectDispatch(conversationEvents),
      messagingService = inject(MessagingService),
    ) => {
      return {
        selectConversation: (profileBrowseId: string, isSuggestedProfile = false) => {
          dispatch.select({ profileBrowseId, isSuggestedProfile })
        },
        clearSelectedConversation: () => dispatch.select({ profileBrowseId: undefined }),
        loadMoreMessages: (conversation: Conversation, next_cursor: string) =>
          dispatch.loadMoreMessages({ conversation, next_cursor }),
        attemptToUnlockConversation: (conversation: Conversation) => {
          if (!messagingService.canAffordConversationUnlock(conversation)) {
            creditsNavigationService.visitCredits('unlockMessage', {
              source: store.source,
              profile_id: conversation.profile.id,
            })
            return
          }

          dispatch.unlock({ conversation })
        },
        removeConversation: (conversation: Conversation) => dispatch.remove({ conversation }),
        isAnchor: (message: Message) => {
          if (message.timestamp === store._anchor_message_timestamp) {
            store._anchor_message_timestamp = undefined
            return true
          }
          return false
        },
      }
    },
  ),
  withReducer(
    on(conversationEvents.select, event => ({
      ...initialState,
      profileBrowseId: event.payload.profileBrowseId,
      isSuggestedProfile: event.payload.isSuggestedProfile ?? false,
    })),
    on(conversationEvents.load, () => ({ loadInProgress: true })),
    on(conversationEvents.loadEnd, () => ({ loadInProgress: false })),
    on(conversationEvents.loadSuccess, event => ({
      conversation: event.payload.response.conversation,
      next_cursor: event.payload.response.next_cursor ?? undefined,
      tip: event.payload.response.tip,
      tip_type: event.payload.response.tip_type,
      outbid: event.payload.response.outbid,
    })),
    on(conversationEvents.loadFailure, () => ({ profileBrowseId: undefined as undefined })),

    on(conversationEvents.loadMoreMessages, () => ({ loadMoreInProgress: true })),
    on(conversationEvents.loadMoreMessagesEnd, () => ({ loadMoreInProgress: false })),
    on(conversationEvents.loadMoreMessagesSuccess, event => ({
      next_cursor: event.payload.response.next_cursor ?? undefined,
      loadMoreInProgress: false,
    })),

    on(conversationEvents.unlock, () => ({ unlockInProgress: true })),
    on(conversationEvents.unlockEnd, () => ({ unlockInProgress: false })),
    on(conversationEvents.unlockSuccess, event => ({ conversation: event.payload.response.conversation })),

    on(conversationEvents.remove, () => ({ removeInProgress: true })),
    on(conversationEvents.removeEnd, () => ({ removeInProgress: false })),

    on(conversationEvents.updateProfile, event => updateConversationProfile(event.payload.profile)),

    on(sendingEvents.sendMessageSuccess, event =>
      updateConversation({
        ...(!event.payload.messages.every(message => message.type === 'photo' && message.status === 'pending')
          ? { read_by_them: false }
          : {}),
        locked: false,
      }),
    ),
    on(sendingEvents.sendRealMessageSuccess, event => updateConversation(event.payload.response.conversation)),
    on(realtimeMessagingEvents.conversationUpdate, ({ payload }) => updateConversation(payload.conversation)),
    on(readReceiptsEvents.unlockReadReceiptsSuccess, event =>
      updateConversation({
        read_receipts_locked: false,
        read_by_them: event.payload.response.read_by_them,
      }),
    ),
  ),

  withEventHandlers(
    (
      store,
      actions$ = inject(Actions),
      conversationService = inject(ConversationService),
      conversationUIService = inject(ConversationUIService),
      creditsStore = inject(CreditsStore),
      creditsNavigationService = inject(CreditsNavigationService),
      defaultErrorHandler = injectDefaultErrorHandler(),
      dispatch = injectDispatch(conversationEvents),
      events = inject(Events),
      globalStore = inject(Store),
      memberService = inject(MemberService),
      messagingService = inject(MessagingService),
      popoverService = inject(PopoverService),
      toastService = inject(ToastService),
      injector = inject(Injector),
      popupService = inject(PopupService),
    ) => ({
      selectConversation$: events.on(conversationEvents.select).pipe(
        map(({ payload }) => payload),
        tap(({ profileBrowseId, isSuggestedProfile }) => {
          if (profileBrowseId !== undefined) {
            dispatch.load({ profileBrowseId, isSuggestedProfile })
          }
        }),
      ),

      load$: events.on(conversationEvents.load).pipe(
        map(({ payload }) => payload),
        switchMap(({ profileBrowseId, isSuggestedProfile }) =>
          conversationService.getConversation(profileBrowseId, undefined, isSuggestedProfile).pipe(
            tapResponse({
              next: response => dispatch.loadSuccess({ response }),
              error: error => dispatch.loadFailure({ profileBrowseId, error }),
              finalize: () => dispatch.loadEnd(),
            }),
          ),
        ),
      ),

      onLoadSuccess$: events.on(conversationEvents.loadSuccess).pipe(
        map(({ payload }) => payload),
        tap(({ response }) => {
          globalStore.dispatch(fromPhotoAccessRequests.Actions.photoAccessRequestsLoaded(response.access_requests))
          globalStore.dispatch(fromProfiles.Actions.oneLoaded({ profile: response.conversation.profile }))

          popupService.addPopupRequests(response.popups)

          if (response.conversation.new_rejected_photos && memberService.account.dependent) {
            conversationUIService.openDisapprovedPhotoModal(
              response.conversation.new_rejected_photos,
              response.conversation,
              injector,
            )
          }
        }),
      ),

      onLoadFailure$: events.on(conversationEvents.loadFailure).pipe(
        map(({ payload }) => payload),
        tap(() => toastService.show("Couldn't load activity.")),
      ),

      loadMoreMessages$: events.on(conversationEvents.loadMoreMessages).pipe(
        map(({ payload }) => payload),
        exhaustMap(({ conversation, next_cursor }) =>
          conversationService.getConversation(conversation.profile.browse_id, next_cursor).pipe(
            tapResponse({
              next: response => dispatch.loadMoreMessagesSuccess({ response }),
              error: error => dispatch.loadMoreMessagesFailure({ error }),
              finalize: () => dispatch.loadMoreMessagesEnd(),
            }),
            takeUntil(events.on(conversationEvents.select)),
          ),
        ),
      ),

      onLoadMoreMessagesFailure$: events.on(conversationEvents.loadMoreMessagesFailure).pipe(
        map(({ payload }) => payload),
        tap(({ error }) => defaultErrorHandler(error)),
      ),

      unlockConversation$: events.on(conversationEvents.unlock).pipe(
        map(({ payload }) => payload),
        exhaustMap(({ conversation }) =>
          conversationService.unlockConversation(conversation.profile.browse_id, store.source).pipe(
            tap(response => creditsStore.onCreditsSpent(response)),
            nonCancelableRequest(),
            tapResponse({
              next: response => dispatch.unlockSuccess({ response }),
              error: error => dispatch.unlockFailure({ error, profile_id: conversation.profile.id }),
              finalize: () => dispatch.unlockEnd(),
            }),
            takeUntil(events.on(conversationEvents.select)),
          ),
        ),
      ),

      onUnlockConversationFailure$: events.on(conversationEvents.unlockFailure).pipe(
        map(({ payload }) => payload),
        tap(({ error, profile_id }) => {
          if (isPaymentRequired(error)) {
            return creditsNavigationService.visitCredits('unlockMessage', { source: store.source, profile_id })
          }

          defaultErrorHandler(error)
        }),
      ),

      removeConversation$: events.on(conversationEvents.remove).pipe(
        map(({ payload }) => payload),
        exhaustMap(({ conversation }) =>
          conversationService.removeConversation(conversation.profile.browse_id).pipe(
            tapResponse({
              next: () => dispatch.removeSuccess({ conversation: store.conversation()! }),
              error: error => dispatch.removeFailure({ error }),
              finalize: () => dispatch.removeEnd(),
            }),
            takeUntil(events.on(conversationEvents.select)),
          ),
        ),
      ),

      onRemoveConversationSuccess$: events.on(conversationEvents.removeSuccess).pipe(
        map(({ payload }) => payload),
        tap(() => {
          popoverService.show()
          store.clearSelectedConversation()
        }),
      ),

      onRemoveConversationFailure$: events.on(conversationEvents.removeFailure).pipe(
        map(({ payload }) => payload),
        tap(({ error }) => defaultErrorHandler(error)),
      ),

      removeConversationOnBlockOrReport$: actions$.pipe(
        ofType(fromProfile.Actions.blocked, fromProfile.Actions.reported),
        filter(({ id }) => id === store.conversation()?.profile.id),
        tap(() => store.clearSelectedConversation()),
      ),

      storeOpenConversation$: toObservable(store.profileBrowseId).pipe(
        switchMap(profileId => (profileId !== undefined ? messagingService.openConversation(profileId) : EMPTY)),
      ),

      onRealtimeConversationReload: events.on(realtimeMessagingEvents.conversationReload).pipe(
        map(({ payload }) => payload),
        tap(() => dispatch.load({ profileBrowseId: store.conversation()!.profile.browse_id })),
      ),

      throwMissingAnchorMessage$: events.on(conversationEvents.loadSuccess).pipe(
        map(({ payload }) => payload),
        tap(({ response }) => {
          if (
            store._anchor_message_timestamp &&
            !response.messages.some(message => message.timestamp === store._anchor_message_timestamp)
          ) {
            store._anchor_message_timestamp = undefined
            ErrorHandlers.handleError(
              new Error(`Message "${store._anchor_message_timestamp}" doesn't exist in response`),
            )
          }
        }),
      ),

      onBidSuccess$: actions$.pipe(
        ofType(fromTopAdmirers.Actions.bidSuccess),
        filter(({ profileId }) => profileId === store.conversation()?.profile.id),
        tap(() => dispatch.load({ profileBrowseId: store.conversation()!.profile.browse_id })),
      ),
    }),
  ),
  withHooks({
    onInit: (_store, messageCacheService = inject(MessageCacheService)) => {
      void messageCacheService.init()
    },
  }),
)
